import { Button, PageHeader, Space } from 'antd'
import React from 'react'
import TableBlock from '../../components/tables/TableBlock'
import { getProductStock } from '../../network/ProductStock'
import { ROLE_PERMISSIONS } from '../../constants/common'
import { useAuth } from '../../hooks/useAuth'
import { createUrlWithFiltersFromKeyRequest } from '../../utils/filterUtils'
import useSearchParams from '../../hooks/useQuery'

/** Normalize name (string or { en, zh }) to a single display string */
const storeNameLabel = (name) =>
  typeof name === 'string' ? name : (name?.en || name?.zh || '')

const ProductStockList = () => {
  const params = useSearchParams()
  const { user } = useAuth()
  const permissions = ROLE_PERMISSIONS[user.role] ? ROLE_PERMISSIONS[user.role] : []

  const storesList = user.stores
    ? (user.franchiseStore
        ? user.stores.filter((s) => s.store === user.currentStore)
        : user.stores)
    : []
  const storeOptions = storesList.map((store) => ({
    label: storeNameLabel(store.name),
    key: store.store,
  }))

  const filters = (history) => ({
    fields: [
      {
        type: 'text',
        label: 'SKU',
        key: 'fixedSku.sku',
      },
      {
        type: 'text',
        label: 'Name (EN)',
        key: 'fixedSku.name.en',
      },
      {
        type: 'text',
        label: 'Name (中文)',
        key: 'fixedSku.name.zh',
      },
      {
        type: 'select',
        label: 'Store',
        key: 'store',
        default: user.franchiseStore ? user.currentStore : '',
        options: storeOptions,
      },
    ],
  })

  const columns = (history, keyRequest) => [
    {
      title: 'Fix SKU',
      key: 'fixedSku.sku',
      dataIndex: ['fixedSku', 'sku'],
      render: (_, record) => record.fixedSku?.sku ?? '–',
      sorter: true,
    },
    {
      title: 'Fix SKU name (EN)',
      key: 'fixedSku.name',
      dataIndex: ['fixedSku', 'name'],
      render: (_, record) => record.fixedSku?.name?.en ?? '–',
      sorter: true,
    },
    {
      title: 'Fix SKU name (中文)',
      key: 'fixedSku.name',
      dataIndex: ['fixedSku', 'name'],
      render: (_, record) => record.fixedSku?.name?.zh ?? '–',
      sorter: true,
    },
    {
      title: 'Store',
      key: 'store',
      dataIndex: ['store', 'name'],
      render: (_, record) => storeNameLabel(record.store?.name) || '–',
      sorter: true,
    },
    {
      title: 'Stock summary',
      key: 'stock',
      dataIndex: 'stock',
      render: (stock) => {
        if (!Array.isArray(stock) || stock.length === 0) return '0'
        const total = stock.reduce((sum, entry) => sum + (Number(entry.stock) || 0), 0)
        const parts = stock.map((e) => `${e.size_name_en || e.size_name_zh || '?'}: ${e.stock ?? 0}`)
        return parts.length <= 2 ? parts.join(', ') : `${total} total (${stock.length} sizes)`
      },
    },
    {
      title: '',
      key: 'action',
      width: 260,
      fixed: 'right',
      render: (_, record) => {
        const pushUrl = (path) => {
          let currentPage = null
          const filterValues = params.get('filterValues')
          if (filterValues) {
            try {
              const allFilters = JSON.parse(decodeURIComponent(filterValues))
              if (allFilters._page) currentPage = parseInt(allFilters._page, 10)
            } catch (err) {}
          }
          if (!currentPage) {
            const pageParam = params.get('page')
            if (pageParam) currentPage = parseInt(pageParam, 10)
          }
          history.push(createUrlWithFiltersFromKeyRequest(path, keyRequest, currentPage))
        }
        return (
          <Space>
            <Button onClick={() => pushUrl(`/fixed-sku-stock/view/${record.id}`)}>
              View
            </Button>
            <Button onClick={() => pushUrl(`/fixed-sku-stock/history/${record.id}`)}>
              History
            </Button>
            <Button
              type="primary"
              onClick={() => pushUrl(`/fixed-sku-stock/manage/${record.id}`)}
            >
              Manage stock
            </Button>
          </Space>
        )
      },
    },
  ]


  return (
    <>
      <PageHeader className="site-page-header" title="Product stock" />
      <TableBlock
        columns={columns}
        filters={filters}
        name="getProductStock"
        request={getProductStock}
        otherFilters={
          user.franchiseStore ? { store: user.currentStore } : {}
        }
      />
    </>
  )
}

export default ProductStockList
